import i18next from 'i18next';
import { Fragment, ReactNode, ReactNodeArray } from 'react';
import reactStringReplace from 'react-string-replace';

import { supportedLangs } from './constants';
import { i18nextInit } from './init';
import { Language, TranslateFnType } from './types';

/**
 * Export get current language helper
 */
export const getCurrentLanguage = () => i18next.language as Language;

/**
 * To translate with injected React components
 * translateUnsafe(t, 'test {{someKey}}', { someKey: () => <div>1</div> })
 * will return: <>test <div>1</div></>
 */
export const translateReact = (
  translateFn: (s: string) => string,
  alias: string,
  options: Record<string, ReactNode>,
) => {
  const translatedString = translateFn(alias);
  const result = Object.entries(options).reduce(
    (
      acc: string | ReactNodeArray,
      [regex, renderer]: [string, ReactNode],
    ): ReactNodeArray => {
      return reactStringReplace(acc, `{{${regex}}}`, (match) => {
        return <Fragment key={match}>{renderer}</Fragment>;
      });
    },
    [translatedString],
  );
  return result as ReactNode;
};

/**
 * Decorator wrapper to add extra logic
 */
export const wrappedTranslateFn = (
  translateFn: TranslateFnType,
  key: string,
  options?: Record<string, unknown>,
) => {
  if (!key || !i18next.language) return key;

  const result = translateFn(key, options);
  if (!result || key === result) return key;

  return String(result);
};

/**
 * To translate with non-React components
 * e.g constants, utils
 * translate('someKey', '@project44/my-app-name', { key: 'val' })
 */
export const translate = (
  key: string,
  ns: string,
  options?: Record<string, unknown>,
) => {
  return wrappedTranslateFn(i18next.t, key, { ns, ...options });
};

/**
 * To get translate function within specified namespace
 * translateFn('@project44/my-app-name', {}, true)
 */
export const getTranslateFn = (
  namespace: string,
  translations?: Record<string, unknown>,
): TranslateFnType => {
  i18nextInit();

  if (translations) {
    supportedLangs.forEach((supportedLang) => {
      // Always override with language from "translation" props
      Object.entries(translations).forEach(([lang, resources]) => {
        if (lang === supportedLang) {
          i18next.addResourceBundle(lang, namespace, resources);
        }
      });

      // Override with English if language file does not exist yet
      const supportedLangBundle = i18next.getResourceBundle(
        supportedLang,
        namespace,
      );

      if (!supportedLangBundle && translations['en']) {
        i18next.addResourceBundle(supportedLang, namespace, translations['en']);
      }
    });
  }

  return (key: string, placeholders?: Record<string, unknown>) => {
    return translate(key, namespace, placeholders);
  };
};

/**
 * To transform locale to language code
 * localeToLang('en-US')
 * will return: en
 */
export const localeToLang = (lang: string | undefined) => {
  if (!lang) return undefined;
  return String(lang.split('-').shift());
};
